import React, { useContext } from 'react';

import { RequiredTag } from '@components/Forms/RequiredTag';
import { TextInput, TextInputProps } from '@components/NextForms/TextInput';
import { MobxZodField, MobxZodTypes } from '@monoid-dev/mobx-zod-form';
import { getForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { getFieldLabel } from '@utils/nextForms';
import { useShallowMemo } from '@utils/useShallowMemo';
import clsx from 'clsx';

const useStyles = makeMuiStyles((theme, common) => ({
  managementFormContainer: {
    display: 'grid',
    gridTemplateColumns: '170px minmax(0,1fr)',
    rowGap: 20,
    columnGap: '1rem',
  },
  managementFormLabel: {
    ...common.flexInline,
    ...common.justifyBetween,
    ...common.middleText,
    ...common.textBlack,
    position: 'relative',
    paddingTop: 5,
    '&$disabled': {
      ...common.textGray500,
    },
  },
  disabled: {},
}));

export interface ManagementFormContextValuesInput {
  /**
   * If 'display', all TextInput will have type="display" which displays only the text content.
   */
  mode: 'edit' | 'display';
  labelProps?: React.ComponentProps<'label'>;
}

const ManagementFormContext = React.createContext<ManagementFormContextValuesInput>(
  undefined as any,
);

export interface ManagementFormContainerProps extends ManagementFormContextValuesInput {
  children: React.ReactNode;
  containerProps?: React.ComponentProps<'div'> & { [K in `${string}-${string}`]: unknown };
}

export const ManagementFormContainer: React.FC<ManagementFormContainerProps> = ({
  children,
  containerProps,
  ...contextValues
}) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <ManagementFormContext.Provider value={useShallowMemo(() => contextValues)}>
      <div
        {...containerProps}
        className={clsx(classes.managementFormContainer, containerProps?.className)}
      >
        {children}
      </div>
    </ManagementFormContext.Provider>
  );
};

export interface ManagementFormLabelProps {
  field: MobxZodField<MobxZodTypes>;
  required?: boolean;
  disabled?: boolean;
}

export const ManagementFormLabel = ({
  field,
  required = false,
  disabled,
}: ManagementFormLabelProps) => {
  const { htmlFor } = getForm(field).bindLabel(field);

  return (
    <ManagementFormLabelInner htmlFor={htmlFor} required={required} disabled={disabled}>
      {getFieldLabel(field)}
    </ManagementFormLabelInner>
  );
};

export const ManagementFormLabelInner: React.FC<
  React.PropsWithChildren<{ htmlFor?: string; required?: boolean; disabled?: boolean }>
> = ({ children, htmlFor, required, disabled }) => {
  const { mode, labelProps } = useContext(ManagementFormContext);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <label
      htmlFor={htmlFor}
      {...labelProps}
      className={clsx(
        classes.managementFormLabel,
        disabled && classes.disabled,
        labelProps?.className,
      )}
    >
      {children}
      {mode === 'edit' && (
        <div style={{ position: 'absolute', right: 0 }}>{required && <RequiredTag />}</div>
      )}
    </label>
  );
};

export type ManagementFormInputProps<T extends MobxZodTypes> = {} & TextInputProps<T>;

export const ManagementFormInput = <T extends MobxZodTypes>(props: ManagementFormInputProps<T>) => {
  const { mode } = useContext(ManagementFormContext);

  return (
    <div>
      <TextInput
        {...props}
        {...(mode === 'display'
          ? { inputOptions: { type: 'display' }, withErrorMessages: false }
          : undefined)}
      />
    </div>
  );
};
